import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useLogin from "../../hooks/useLogin";
import {
  PreferenciasTablaGet,
  PreferenciasTablaSave,
} from "../../service/preferencias";

const ESPERA_PARA_GUARDAR = 700;

/**
 * Columnas visibles de una tabla, guardadas en la base por usuario.
 *
 * Lo que se guarda son las columnas **ocultas**, no las visibles. Si se
 * guardaran las visibles, el día que alguien agrega una columna nueva a una
 * pantalla nadie que ya tuviera preferencia la vería: no estaría en su lista.
 * Guardando las ocultas, lo nuevo aparece solo.
 *
 * Mientras no llega la preferencia se muestran todas, para que la tabla no
 * "salte" ni aparezca a medias.
 *
 * El guardado espera 700 ms: marcar tres columnas seguidas manda una petición,
 * no tres.
 */
export default function useColumnasVisibles(tableKey, columns) {
  const { token } = useLogin();
  const [ocultas, setOcultas] = useState([]);
  const temporizador = useRef(null);

  const todas = useMemo(() => columns.map((c) => c.field), [columns]);

  useEffect(() => {
    if (!tableKey || !token) return;

    let cancelado = false;

    PreferenciasTablaGet(tableKey, token)
      .then((respuesta) => {
        const columnas = respuesta?.data?.columnas;
        if (cancelado || !Array.isArray(columnas)) return;

        // Se ignoran las que ya no existen en la pantalla.
        setOcultas(columnas.filter((campo) => todas.includes(campo)));
      })
      .catch(() => {
        // Si falla, la tabla se queda con todas las columnas: no vale la pena
        // molestar al usuario por una preferencia de vista.
      });

    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey, token]);

  useEffect(() => () => window.clearTimeout(temporizador.current), []);

  const guardar = useCallback(
    (columnas) => {
      if (!tableKey || !token) return;

      window.clearTimeout(temporizador.current);
      temporizador.current = window.setTimeout(() => {
        PreferenciasTablaSave({ tabla: tableKey, columnas }, token).catch(() => {});
      }, ESPERA_PARA_GUARDAR);
    },
    [tableKey, token]
  );

  const visibles = useMemo(
    () => todas.filter((campo) => !ocultas.includes(campo)),
    [todas, ocultas]
  );

  const alternar = useCallback(
    (campo) => {
      const nuevas = ocultas.includes(campo)
        ? ocultas.filter((f) => f !== campo)
        : [...ocultas, campo];

      // Nunca dejar la tabla sin una sola columna.
      if (nuevas.length >= todas.length) return;

      setOcultas(nuevas);
      guardar(nuevas);
    },
    [ocultas, todas, guardar]
  );

  const mostrarTodas = useCallback(() => {
    setOcultas([]);
    guardar([]);
  }, [guardar]);

  const columnasVisibles = useMemo(
    () => columns.filter((c) => visibles.includes(c.field)),
    [columns, visibles]
  );

  return { visibles, columnasVisibles, alternar, mostrarTodas };
}
